import type { ModalSDK } from '../modal/modal.types'
import type { Params } from '../params/params.types'
import type HostPageEventInstrumentation from '../host-page-events/host-page-event.instrumentation'
import type { InlineGridTelemetry } from './inline-grid-telemetry'
import {
  donateCTAClickedEvent,
  type HostedDonationGridViewedEventData,
  hostedDonationGridViewedEvent,
  studioGridAmountSelectionEvent,
  studioGridButtonClickedEvent,
  studioGridCurrencySelectionEvent,
  studioGridFrequencyToggleEvent,
  studioGridValueSelectedEvent,
} from '../host-page-events/host-page-event.definitions'
import {
  INLINE_GRID_MESSAGE_TYPE,
  INLINE_GRID_INTERACTIONS,
  InlineGridAnalyticsPayload,
  InlineGridMessageFromGrid,
  InlineGridLiveSelection,
  InlineGridSelection,
  InlineGridSelectionUpdate,
  isInlineGridMessageFromGrid,
  isUnchangedSelectionField,
  makeInlineGridMessage,
  toInlineGridSelection,
  toInlineGridSelectionUpdate,
} from './inline-grid-protocol.types'

interface InlineGridProtocolControllerProps {
  hostPageEventInstrumentation: HostPageEventInstrumentation
  modalSDK: ModalSDK
  readyTimeoutMs?: number
  recordTelemetry?: (record: InlineGridTelemetry) => void
  win: Window
}

interface InlineGridRegistration {
  campaignId: string
  componentId: string
  iframe: HTMLIFrameElement
  instanceId: string
  onUnavailable: () => void
  targetOrigin: string
}

interface InlineGridSession extends InlineGridRegistration {
  attemptId: string
  startedAt: number
  wasDeferred: boolean
  amountByFrequency: Record<string, number>
  cleanupLoadListener: () => void
  cleanupToggleListener?: () => void
  currentSelection?: InlineGridSelection
  hasMeasuredHeight: boolean
  isReady: boolean
  loadedAt?: number
  helloAt?: number
  readyAt?: number
  liveSelection?: InlineGridLiveSelection
  readyTimeout?: ReturnType<typeof setTimeout>
  resizeObserver?: ResizeObserver
  viewedEventData?: HostedDonationGridViewedEventData
  viewedTracked: boolean
}

const DEFAULT_READY_TIMEOUT_MS = 10000

const formatAmountSelectionValue = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(Math.round(amount))

export default class InlineGridProtocolController {
  private readonly hostPageEventInstrumentation: HostPageEventInstrumentation
  private readonly modalSDK: ModalSDK
  private readonly readyTimeoutMs: number
  private readonly sessions = new Map<string, InlineGridSession>()
  private readonly win: Window
  private destroyed = false
  private instanceSequence = 0
  private attemptSequence = 0
  private readonly attemptPrefix = `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}`
  private readonly recordTelemetry?: (record: InlineGridTelemetry) => void

  constructor({
    hostPageEventInstrumentation,
    modalSDK,
    readyTimeoutMs = DEFAULT_READY_TIMEOUT_MS,
    recordTelemetry,
    win,
  }: InlineGridProtocolControllerProps) {
    this.hostPageEventInstrumentation = hostPageEventInstrumentation
    this.modalSDK = modalSDK
    this.readyTimeoutMs = readyTimeoutMs
    this.recordTelemetry = recordTelemetry
    this.win = win
    this.win.addEventListener('message', this.handleMessage)
    this.win.document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  public createInstanceId(campaignId: string, componentId: string): string {
    this.instanceSequence += 1
    const safeCampaignId = campaignId.replace(/[^A-Za-z0-9._:-]/g, '-')
    const safeComponentId = componentId.replace(/[^A-Za-z0-9._:-]/g, '-')

    return `${safeCampaignId}:${safeComponentId}:${this.instanceSequence}`
  }

  public register(registration: InlineGridRegistration): (failureReason?: 'grid_error') => void {
    if (this.destroyed) {
      return () => undefined
    }

    this.unregister(registration.instanceId)

    const handleLoad = () => {
      const session = this.sessions.get(registration.instanceId)
      if (session && session.loadedAt === undefined) {
        session.loadedAt = this.win.performance.now()
      }
      this.sendInitialize(registration.instanceId)
    }

    registration.iframe.addEventListener('load', handleLoad)

    const session: InlineGridSession = {
      ...registration,
      attemptId: `${this.attemptPrefix}-${++this.attemptSequence}`,
      startedAt: this.win.performance.now(),
      wasDeferred: false,
      amountByFrequency: {},
      cleanupLoadListener: () => registration.iframe.removeEventListener('load', handleLoad),
      hasMeasuredHeight: false,
      isReady: false,
      viewedTracked: false,
    }

    this.sessions.set(registration.instanceId, session)
    this.recordGridAttempt(session)
    // Anchor fail-closed to registration so a navigation that never emits load or HELLO
    // cannot leave an empty iframe mounted indefinitely.
    this.startReadyTimeout(registration.instanceId)
    const ResizeObserverClass = (this.win as Window & typeof globalThis).ResizeObserver
    if (typeof ResizeObserverClass === 'function') {
      session.resizeObserver = new ResizeObserverClass(() => this.updateReadyTimeout(session))
      session.resizeObserver.observe(session.iframe)
      // Closed <details> can retain its width, so a reveal need not trigger ResizeObserver.
      const handleToggle = (event: Event) => {
        const target = event.target as HTMLElement | null
        if (target?.tagName === 'DETAILS' && target.contains(session.iframe)) {
          this.updateReadyTimeout(session)
        }
      }
      this.win.document.addEventListener('toggle', handleToggle, true)
      session.cleanupToggleListener = () =>
        this.win.document.removeEventListener('toggle', handleToggle, true)
      this.updateReadyTimeout(session)
    }

    return (failureReason) => {
      if (this.sessions.get(registration.instanceId) === session) {
        if (failureReason) {
          this.recordGridAttempt(session, 'fallback', failureReason)
        }
        this.unregister(registration.instanceId)
      }
    }
  }

  public syncSelection(campaignId: string, params: Params): void {
    if (this.destroyed) {
      return
    }

    const incoming = toInlineGridSelectionUpdate(params)

    if (!incoming) {
      return
    }

    this.sessions.forEach((session) => {
      if (!session.isReady || session.campaignId !== campaignId) {
        return
      }

      const update = this.normalizeSelectionUpdate(session, incoming)

      if (!update) {
        return
      }

      this.postToGrid(
        session,
        makeInlineGridMessage(INLINE_GRID_MESSAGE_TYPE.SELECTION_UPDATE, session.instanceId, update)
      )
    })
  }

  public getSelectionParams(campaignId: string): Params | undefined {
    const sessions = Array.from(this.sessions.values()).filter(
      (session) => session.campaignId === campaignId && session.iframe.isConnected
    )

    // A campaign-only link cannot identify which of two same-campaign grids the donor meant.
    if (this.destroyed || sessions.length !== 1 || !sessions[0].isReady) {
      return undefined
    }

    const selection = sessions[0].liveSelection
    if (!selection || selection.amount === null) {
      return undefined
    }

    return {
      amount: String(selection.amount),
      currency: selection.currency,
      frequency: selection.frequency,
    }
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true
    this.win.removeEventListener('message', this.handleMessage)
    this.win.document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    Array.from(this.sessions.keys()).forEach((instanceId) => this.unregister(instanceId))
  }

  private handleMessage = (event: MessageEvent): void => {
    if (this.destroyed || !isInlineGridMessageFromGrid(event.data)) {
      return
    }

    const message = event.data
    const session = this.sessions.get(message.instanceId)

    if (
      !session ||
      event.source !== session.iframe.contentWindow ||
      event.origin !== session.targetOrigin
    ) {
      return
    }

    this.handleTrustedMessage(session, message)
  }

  private handleTrustedMessage(
    session: InlineGridSession,
    message: InlineGridMessageFromGrid
  ): void {
    switch (message.type) {
      case INLINE_GRID_MESSAGE_TYPE.ANALYTICS:
        // The iframe can become interactive just before READY completes. Source, origin,
        // instance, schema, and campaign are already validated, so accept those early
        // best-effort events instead of silently dropping the donor's first interaction.
        if (message.payload.campaignId !== session.campaignId) {
          return
        }
        this.handleAnalytics(session, message.payload)
        return
      case INLINE_GRID_MESSAGE_TYPE.HELLO:
        if (message.payload.campaignId === session.campaignId) {
          session.helloAt ??= this.win.performance.now()
          this.sendInitialize(session.instanceId)
        }
        return
      case INLINE_GRID_MESSAGE_TYPE.READY:
        if (message.payload.campaignId !== session.campaignId || session.isReady) {
          return
        }
        session.isReady = true
        session.readyAt = this.win.performance.now()
        this.updateReadyTimeout(session)
        return
      case INLINE_GRID_MESSAGE_TYPE.RESIZE:
        if (!session.isReady) {
          return
        }
        this.applyHeight(session.iframe, message.payload.height)
        if (!session.hasMeasuredHeight) {
          this.recordGridAttempt(session, 'ready')
          session.hasMeasuredHeight = true
          this.stopHeightWatchdog(session)
          this.trackViewedIfReady(session)
        }
        return
      case INLINE_GRID_MESSAGE_TYPE.SUBMIT:
        if (!session.isReady || message.payload.campaignId !== session.campaignId) {
          return
        }
        this.handleSubmit(session, message.payload)
        return
      case INLINE_GRID_MESSAGE_TYPE.SELECTION_UPDATE:
        session.liveSelection = message.payload
        if (message.payload.amount !== null) {
          session.currentSelection = {
            ...session.currentSelection,
            ...message.payload,
            amount: message.payload.amount,
          }
          session.amountByFrequency[message.payload.frequency] = message.payload.amount
        } else {
          session.currentSelection = undefined
        }
        return
      case INLINE_GRID_MESSAGE_TYPE.ERROR:
        console.warn('[SDK Inline Grid] Whammy reported a grid error:', {
          code: message.payload.code,
          instanceId: session.instanceId,
          phase: message.payload.phase,
          reason: message.payload.reason,
        })
        if (
          message.payload.phase === 'initialize' ||
          message.payload.phase === 'render' ||
          (message.payload.code === 'GRID_SUBMIT_FAILED' &&
            message.payload.phase === 'submit' &&
            message.payload.reason === 'selection_invalid')
        ) {
          this.failSession(session.instanceId)
        }
        return
      default:
        return
    }
  }

  private handleSubmit(
    session: InlineGridSession,
    selection: InlineGridSelection & { campaignId: string }
  ): void {
    session.currentSelection = selection
    session.amountByFrequency[selection.frequency] = selection.amount

    const params: Params = {
      amount: String(selection.amount),
      currency: selection.currency,
      frequency: selection.frequency,
      // SUBMIT is the full grid selection; omission would preserve a cached modal dedication.
      dedication: selection.dedication?.text ?? '',
      ...(selection.dedication !== undefined && {
        dedicationType: selection.dedication.type,
      }),
      ...(selection.designation !== undefined && {
        designation: String(selection.designation),
      }),
      ...(selection.recurringEndDate !== undefined && {
        recurringEndDate: selection.recurringEndDate,
      }),
    }

    this.trackBestEffort(() => {
      this.hostPageEventInstrumentation.track(donateCTAClickedEvent, {
        campaignId: session.campaignId,
        componentId: session.componentId,
        data: {
          donation_amount: selection.amount,
          donation_currency: selection.currency,
          donation_frequency: selection.frequency,
          link_url: this.win.location.href,
          text: selection.frequency === 'monthly' ? 'Donate monthly' : 'Donate one time',
        },
      })
    })

    this.modalSDK.open(session.campaignId, params)
  }

  private handleAnalytics(session: InlineGridSession, payload: InlineGridAnalyticsPayload): void {
    this.trackBestEffort(() => {
      switch (payload.action) {
        case 'amount_change': {
          const isCustomAmount = payload.donationType === 'custom'
          this.hostPageEventInstrumentation.track(studioGridAmountSelectionEvent, {
            campaignId: session.campaignId,
            componentId: session.componentId,
            data: {
              ...(!isCustomAmount && { donation_amount: payload.toAmount }),
              from_amount: payload.fromAmount,
              to_amount: payload.toAmount,
              value: isCustomAmount
                ? 'Custom'
                : formatAmountSelectionValue(payload.toAmount, payload.currency),
            },
          })
          return
        }
        case 'currency_change':
          this.hostPageEventInstrumentation.track(studioGridCurrencySelectionEvent, {
            campaignId: session.campaignId,
            componentId: session.componentId,
            data: {
              from_currency: payload.fromCurrency,
              to_currency: payload.toCurrency,
            },
          })
          return
        case 'frequency_change':
          this.hostPageEventInstrumentation.track(studioGridFrequencyToggleEvent, {
            campaignId: session.campaignId,
            componentId: session.componentId,
            data: {
              from_frequency: payload.fromFrequency,
              to_frequency: payload.toFrequency,
            },
          })
          return
        case 'interaction': {
          const eventName = INLINE_GRID_INTERACTIONS[payload.trackingId]
          const definition =
            eventName === 'Button Clicked'
              ? studioGridButtonClickedEvent
              : studioGridValueSelectedEvent
          this.hostPageEventInstrumentation.track(definition, {
            campaignId: session.campaignId,
            componentId: session.componentId,
            data: { tracking_id: payload.trackingId },
          })
          return
        }
        case 'viewed': {
          session.viewedEventData = {
            donation_amount_default: payload.donation_amount_default,
            donation_amount_options: payload.donation_amount_options,
            donation_currency_default: payload.donation_currency_default,
            donation_currency_options: payload.donation_currency_options,
            donation_frequency_default: payload.donation_frequency_default,
            donation_frequency_options: payload.donation_frequency_options,
          }
          this.trackViewedIfReady(session)
          return
        }
      }
    })
  }

  private trackViewedIfReady(session: InlineGridSession): void {
    const viewedEventData = session.viewedEventData

    if (
      !session.isReady ||
      !session.hasMeasuredHeight ||
      !viewedEventData ||
      session.viewedTracked
    ) {
      return
    }

    session.viewedTracked = true
    this.trackBestEffort(() => {
      this.hostPageEventInstrumentation.track(hostedDonationGridViewedEvent, {
        campaignId: session.campaignId,
        componentId: session.componentId,
        data: viewedEventData,
      })
    })
  }

  private trackBestEffort(track: () => void): void {
    try {
      track()
    } catch {
      // Analytics is best-effort and must never interrupt the donation flow.
    }
  }

  private normalizeSelectionUpdate(
    session: InlineGridSession,
    incoming: InlineGridSelectionUpdate
  ): InlineGridSelectionUpdate | null {
    const current = session.currentSelection

    if (!current) {
      // A modal opened outside the grid (link, button) has no submitted selection to diff
      // against. Remember the first update so later ones can be reduced to what changed —
      // otherwise every keystroke in the modal's dedication field re-seeds the whole grid.
      session.currentSelection = toInlineGridSelection(incoming)

      return incoming
    }

    const update: InlineGridSelectionUpdate = { ...incoming }
    const previousFrequency = current.frequency
    const nextFrequency = incoming.frequency || previousFrequency

    if (incoming.frequency && incoming.frequency !== previousFrequency) {
      if (incoming.amount !== undefined) {
        session.amountByFrequency[previousFrequency] = incoming.amount
      }

      const restoredAmount = session.amountByFrequency[nextFrequency]
      if (restoredAmount !== undefined) {
        update.amount = restoredAmount
      } else {
        delete update.amount
      }
    } else if (incoming.amount !== undefined) {
      session.amountByFrequency[nextFrequency] = incoming.amount
    }

    const updateKeys = Object.keys(update) as Array<keyof InlineGridSelectionUpdate>
    updateKeys.forEach((key) => {
      if (isUnchangedSelectionField(key, update, current)) {
        delete update[key]
      }
    })

    if (Object.keys(update).length === 0) {
      return null
    }

    const { dedication, ...updateWithoutDedication } = update
    const nextSelection: InlineGridSelection = {
      ...current,
      ...updateWithoutDedication,
      frequency: nextFrequency,
    }

    if (dedication === null) {
      delete nextSelection.dedication
    } else if (dedication !== undefined) {
      nextSelection.dedication = dedication
    }

    session.currentSelection = nextSelection

    return update
  }

  private applyHeight(iframe: HTMLIFrameElement, height: number): void {
    const nextHeight = `${Math.ceil(height)}px`

    if (iframe.style.height !== nextHeight) {
      iframe.style.height = nextHeight
    }
  }

  private sendInitialize(instanceId: string): void {
    const session = this.sessions.get(instanceId)

    if (!session || session.isReady) {
      return
    }

    this.postToGrid(
      session,
      makeInlineGridMessage(INLINE_GRID_MESSAGE_TYPE.INITIALIZE, instanceId, {
        campaignId: session.campaignId,
      })
    )
  }

  private startReadyTimeout(instanceId: string): void {
    const session = this.sessions.get(instanceId)

    if (!session || session.hasMeasuredHeight || session.readyTimeout) {
      return
    }

    session.readyTimeout = setTimeout(() => {
      const current = this.sessions.get(instanceId)

      if (!current || current.hasMeasuredHeight) {
        return
      }
      current.readyTimeout = undefined
      // Recheck here as observer/visibility notifications can arrive after the timer.
      if (this.isHeightWaitSuspended(current)) {
        current.wasDeferred = true
        // A hide/reveal between rendering steps can leave the last reported size unchanged.
        current.resizeObserver?.unobserve(current.iframe)
        current.resizeObserver?.observe(current.iframe)
        return
      }

      console.warn(
        '[SDK Inline Grid] Grid did not become ready with a measured height before timeout:',
        {
          code: 'GRID_INITIALIZATION_FAILED',
          instanceId,
          phase: 'initialize',
        }
      )
      this.failSession(instanceId, 'timeout')
    }, this.readyTimeoutMs)
  }

  private isHeightWaitSuspended(session: InlineGridSession): boolean {
    // Older browsers keep the existing deadline, without polling or a polyfill.
    if (!session.resizeObserver) {
      return false
    }
    if (this.win.document.visibilityState === 'hidden') {
      return true
    }
    // HELLO/READY do not need layout. Hidden content must still recover from a failed load.
    if (!session.isReady) {
      return false
    }

    const iframe = session.iframe as HTMLIFrameElement & { checkVisibility?: () => boolean }
    return (
      !iframe.isConnected ||
      iframe.getBoundingClientRect().width <= 0 ||
      (typeof iframe.checkVisibility === 'function' && !iframe.checkVisibility())
    )
  }

  private updateReadyTimeout(session: InlineGridSession): void {
    if (this.sessions.get(session.instanceId) !== session || session.hasMeasuredHeight) {
      return
    }

    if (this.isHeightWaitSuspended(session)) {
      // Registration runs before insertion; that brief detached state is not a hidden grid.
      if (session.iframe.isConnected || this.win.document.visibilityState === 'hidden') {
        session.wasDeferred = true
      }
      clearTimeout(session.readyTimeout)
      session.readyTimeout = undefined
    } else {
      // Starting only when absent gives each reveal a full deadline; width changes do not reset it.
      this.startReadyTimeout(session.instanceId)
    }
  }

  private handleVisibilityChange = (): void => {
    this.sessions.forEach((session) => this.updateReadyTimeout(session))
  }

  private stopHeightWatchdog(session: InlineGridSession): void {
    clearTimeout(session.readyTimeout)
    session.readyTimeout = undefined
    session.resizeObserver?.disconnect()
    session.resizeObserver = undefined
    session.cleanupToggleListener?.()
    session.cleanupToggleListener = undefined
  }

  private recordGridAttempt(
    session: InlineGridSession,
    outcome?: 'ready' | 'fallback',
    reason?: InlineGridTelemetry['reason']
  ): void {
    if (session.hasMeasuredHeight || !this.recordTelemetry) {
      return
    }
    try {
      const durationMs = outcome ? this.win.performance.now() - session.startedAt : 0
      const diagnostics = outcome ? this.getGridDiagnostics(session) : undefined
      this.recordTelemetry({
        attemptId: session.attemptId,
        campaignId: session.campaignId,
        sdkBuild: process.env.SDK_BUILD_ID || 'unknown',
        phase: outcome ? 'result' : 'start',
        durationMs,
        wasDeferred: session.wasDeferred,
        ...(outcome && { outcome }),
        ...(reason && { reason }),
        ...(diagnostics && { diagnostics }),
      })
    } catch {
      // Operational diagnostics must not change rendering or fallback.
    }
  }

  private getGridDiagnostics(session: InlineGridSession): InlineGridTelemetry['diagnostics'] {
    try {
      const iframe = session.iframe as HTMLIFrameElement & { checkVisibility?: () => boolean }
      const size = iframe.getBoundingClientRect()
      const elapsed = (time: number | undefined): number | null =>
        time === undefined ? null : time - session.startedAt
      let handshakeStage: 'none' | 'hello' | 'ready' = 'none'
      if (session.isReady) {
        handshakeStage = 'ready'
      } else if (session.helloAt !== undefined) {
        handshakeStage = 'hello'
      }

      return {
        handshakeStage,
        loadMs: elapsed(session.loadedAt),
        helloMs: elapsed(session.helloAt),
        readyMs: elapsed(session.readyAt),
        iframeWidth: Math.round(size.width),
        iframeHeight: Math.round(size.height),
        iframeConnected: iframe.isConnected,
        tabVisible: this.win.document.visibilityState === 'visible',
        layoutVisible:
          typeof iframe.checkVisibility === 'function' ? iframe.checkVisibility() : null,
        timeoutMs: this.readyTimeoutMs,
      }
    } catch {
      // A host-page layout failure must not discard the existing lifecycle record.
      return undefined
    }
  }

  private failSession(
    instanceId: string,
    reason: InlineGridTelemetry['reason'] = 'grid_error'
  ): void {
    const session = this.sessions.get(instanceId)

    if (!session) {
      return
    }

    const { onUnavailable } = session
    this.recordGridAttempt(session, 'fallback', reason)
    this.unregister(instanceId)
    onUnavailable()
  }

  private postToGrid(session: InlineGridSession, message: unknown): void {
    session.iframe.contentWindow?.postMessage(message, session.targetOrigin)
  }

  private unregister(instanceId: string): void {
    const session = this.sessions.get(instanceId)

    if (!session) {
      return
    }

    session.cleanupLoadListener()
    this.stopHeightWatchdog(session)
    this.sessions.delete(instanceId)
  }
}
